import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Permission, PermissionSubject, PermissionVerb } from '@s8l/client-tree-lib';
import { Subscription, combineLatest } from 'rxjs';
import { VRSpeechTrainerModalComponent } from 'src/app/components/vrspeech-trainer-modal/vrspeech-trainer-modal.component';
import { CmsService } from 'src/app/services/cms.service';
import { DurationService } from 'src/app/services/duration.service';
import { Licence, Profile } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';
import { VstServerService, VstTrainer } from 'src/app/services/jsonrpc/vst-server.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-vrspeech',
  templateUrl: './vrspeech.component.html',
  styleUrls: ['./vrspeech.component.scss']
})
export class VRSpeechComponent implements OnInit, OnDestroy {
  public get canEdit(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Write));
  }

  public get canDelete(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Delete));
  }

  private subscription: Subscription;

  public trainers: VstTrainer[];
  public userLookup: { [uuid: string]: Profile } = {};
  public licenceLookup: { [key: string]: Licence } = {};

  constructor(
    public duration: DurationService,
    private cms: CmsService,
    private users: UserService,
    private vst: VstServerService,
    private modal: NgbModal
  ) {}

  public ngOnInit(): Promise<any> {
    this.subscription = combineLatest([this.users.users, this.users.templates]).subscribe(([usr, tmpl]) => {
      this.userLookup = usr.reduce((lookup, u) => {
        lookup[u.uuid] = u;
        return lookup;
      }, {} as { [uuid: string]: Profile });
      this.licenceLookup = tmpl
        .filter(e => e.key != null)
        .map(e => e as Licence)
        .filter(e => e != null)
        .reduce((lookup, l) => {
          lookup[l.key] = l;
          return lookup;
        }, {} as { [key: string]: Licence });
    });

    return this.reload();
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public reload() {
    this.trainers = null;
    return this.vst.list().then(t => (this.trainers = t));
  }

  public create() {
    return this.update(null);
  }

  public update(trainer: Profile) {
    const modalRef = this.modal.open(VRSpeechTrainerModalComponent, { size: 'lg' });
    modalRef.componentInstance.profile = trainer;
    modalRef.componentInstance.profiles = Object.values(this.userLookup);
    modalRef.componentInstance.licences = Object.values(this.licenceLookup);
    return modalRef.componentInstance.onSubmit.subscribe((evt: { profile: Profile; licence: Licence }) =>
      this.vst.add(evt.profile.uuid, evt.licence.key).then(_ => this.reload())
    );
  }

  public removeLicence(user: string, licence: string): Promise<any> {
    return this.vst.remove(user, licence).then(_ => this.reload());
  }

  public deleteTrainer(user: string): Promise<any> {
    return Swal.fire({
      title: 'Remove trainer privileges?',
      text: 'The user will not be able to manage users in the VST platform anymore. Do you want to continue?',
      icon: 'question',
      showCancelButton: true,
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-primary me-1',
        cancelButton: 'btn btn-danger'
      }
    }).then(result => {
      if (result.isConfirmed) {
        return this.vst.delete(user).then(_ => this.reload());
      }
    });
  }
}
